import React from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '@shared/ui/card';
import { Button } from '@shared/ui/button';
import { MessageSquare, Wrench, RefreshCw, ArrowRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useConsumerRequests } from '../../../requests/hook';
import { getLoginDataFromStorage } from '@shared/utils/loginUtils';
import { getRemoteUtilityId } from '@shared/utils/getUtilityId';
import { useTranslation } from 'react-i18next';

export const RecentRequestsWidget = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { consumerId } = getLoginDataFromStorage();

  const { data: requestsData, isLoading } = useConsumerRequests({
    consumer_id: consumerId,
    remote_utility_id: getRemoteUtilityId(),
    limit: 5
  });

  const recentRequests = requestsData?.results || [];

  const getRequestStyle = (type: string) => {
    switch (type?.toLowerCase()) {
      case 'complaint':
        return { icon: <MessageSquare size={20} />, color: 'text-orange-600', bg: 'bg-orange-50' };
      case 'service':
        return { icon: <Wrench size={20} />, color: 'text-blue-600', bg: 'bg-blue-50' };
      case 'transfer':
        return { icon: <RefreshCw size={20} />, color: 'text-indigo-600', bg: 'bg-indigo-50' };
      default:
        return { icon: <MessageSquare size={20} />, color: 'text-slate-600', bg: 'bg-slate-50' };
    }
  };

  return (
    <Card className="rounded-2xl shadow-sm border-slate-200 h-full flex flex-col">
      <CardHeader className="flex flex-row items-center justify-between pb-2">
        <CardTitle className="text-lg font-bold text-slate-800">{t("Recent Requests")}</CardTitle>
        <Button variant="ghost" size="sm" className="text-blue-600 font-semibold h-8 px-2" onClick={()=>navigate("/dashboard/requests")}>{t("View All")} <ArrowRight size={16} className="ml-1" /></Button>
      </CardHeader>
      <CardContent className="flex-1 flex flex-col gap-3">
        {isLoading ? (
          <div className="flex-1 flex items-center justify-center text-sm text-slate-500">{t("Loading requests...")}</div>
        ) : recentRequests.length > 0 ? (
          recentRequests.map((req: any, idx: number) => {
            const style = getRequestStyle(req.requestType || req.requestTypeDisplay);
            return (
              <div key={idx} className="flex items-center justify-between p-3 rounded-xl border border-slate-100 hover:bg-slate-50 transition-colors cursor-pointer" onClick={() => navigate("/dashboard/requests")}>
                <div className="flex items-center gap-3">
                  <div className={`p-2 rounded-lg ${style.bg} ${style.color}`}>
                    {style.icon}
                  </div>
                  <div>
                    <p className="text-sm font-bold text-slate-800">{req.requestSubtypeDisplay || req.requestSubtype || req.title || t("Request")}</p>
                    <p className="text-xs font-medium text-slate-500">{req.requestTypeDisplay || req.requestType || t("General")} • {req.requestNo || req.id}</p>
                  </div>
                </div>
                <span className="text-[10px] font-bold tracking-wider px-2 py-1 rounded-full bg-slate-100 text-slate-600">
                  {req.statusDisplay || req.status || t("Pending")}
                </span>
              </div>
            );
          })
        ) : (
          <div className="flex-1 flex items-center justify-center text-sm text-slate-500">{t("No recent requests")}</div>
        )}
      </CardContent>
    </Card>
  );
};
